import React, { useState, useEffect } from 'react';
import { FaSearch } from 'react-icons/fa';
import { TextField, InputAdornment, Autocomplete, CircularProgress, Chip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import useDeliveryPositions from '../../../hooks/useDeliveryPositions';
import { cvManagementApi } from '../../../services/api/cvManagementApi';
import { COMPARISON_OPERATOR_OPTIONS, DEFAULT_COMPARISON_OPERATOR } from '../../../constants/comparisonOperators';
import SkillsFilter from './SkillsFilter';

const CVRepositoryFilters = ({ 
  searchInputValue, 
  setSearchInputValue, 
  selectedPositions, 
  setSelectedPositions, 
  linkedFilter, 
  setLinkedFilter,
  selectedLanguage,
  setSelectedLanguage,
  selectedLevel,
  setSelectedLevel,
  levelComparator,
  setLevelComparator,
  selectedSkills,
  setSelectedSkills
}) => {
  const { positions, loading } = useDeliveryPositions();
  
  // Language and level data
  const [languages, setLanguages] = useState([]);
  const [languageLevels, setLanguageLevels] = useState([]);
  const [languagesLoading, setLanguagesLoading] = useState(true);
  const [levelsLoading, setLevelsLoading] = useState(true);

  // Fetch languages and levels on component mount
  useEffect(() => {
    const fetchLanguagesAndLevels = async () => {
      try {
        const [languagesResponse, levelsResponse] = await Promise.all([
          cvManagementApi.getAllLanguages(),
          cvManagementApi.getAllLanguageLevels()
        ]);
        
        setLanguages(languagesResponse || []);
        setLanguageLevels(levelsResponse || []);
      } catch (error) {
        console.error('Error fetching languages and levels:', error);
      } finally {
        setLanguagesLoading(false);
        setLevelsLoading(false);
      }
    };
    
    fetchLanguagesAndLevels();
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      {/* First Row - Search, Positions, Employee Link */}
      <div style={{ display: 'flex', gap: '16px', width: '100%', alignItems: 'flex-end' }}>
        <div style={{ width: '25%' }}>
          <TextField
            placeholder="Search by Name, Country, City or CV code ..."
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch style={{ color: '#6B7280' }} />
                  </InputAdornment>
                )
              }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                height: '40px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563eb',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563eb',
                }
              },
              '& .MuiInputBase-input': {
                padding: '8px 14px',
              }
            }}
          />
        </div>
        <div style={{ width: '35%' }}>
          <Autocomplete
            multiple
            options={positions || []}
            getOptionLabel={(option) => option.label || ''}
            value={selectedPositions}
            onChange={(_, newValue) => setSelectedPositions(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedPositions.length === 0 ? "Select Delivery Positions ..." : ""}
                sx={{
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 0',
                  }
                }}
              />
            )}
            loading={loading}
            loadingText="Loading positions..."
            sx={{
              '& .MuiChip-root': {
                backgroundColor: '#EFF6FF',
                color: '#2563EB'
              }
            }}
          />
        </div>
        <div style={{ width: '20%' }}>
          <FormControl fullWidth>
            <InputLabel id="linked-filter-label" sx={{ fontSize: '14px' }}>Individual Link</InputLabel>
            <Select
              labelId="linked-filter-label"
              value={linkedFilter === null ? 'all' : linkedFilter ? 'linked' : 'unlinked'}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'all') {
                  setLinkedFilter(null);
                } else if (value === 'linked') {
                  setLinkedFilter(true);
                } else {
                  setLinkedFilter(false);
                }
              }}
              label="Individual Link"
              sx={{
                backgroundColor: 'white',
                height: '40px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D1D5DB',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563eb',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563eb',
                },
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
            >
              <MenuItem value="all">All CVs</MenuItem>
              <MenuItem value="linked">Linked to Individual</MenuItem>
              <MenuItem value="unlinked">Not Linked</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      {/* Second Row - Language, Level, Level Comparator */}
      <div style={{ display: 'flex', gap: '16px', width: '100%', alignItems: 'flex-end' }}>
        <div style={{ width: '35%' }}>
          <Autocomplete
            options={languagesLoading ? [] : languages}
            getOptionLabel={(option) => option.name || ''}
            value={selectedLanguage}
            onChange={(_, newValue) => {
              setSelectedLanguage(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Language"
                placeholder="Select a language..."
                sx={{
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 0',
                  }
                }}
              />
            )}
            loading={languagesLoading}
            loadingText="Loading languages..."
            sx={{
              '& .MuiChip-root': {
                backgroundColor: '#EFF6FF',
                color: '#2563EB'
              }
            }}
          />
        </div>

        <div style={{ width: '20%' }}>
          <FormControl fullWidth>
            <InputLabel 
              id="level-comparator-label" 
              sx={{ fontSize: '14px' }}
            >
              Level Comparison
            </InputLabel>
            <Select
              labelId="level-comparator-label"
              value={levelComparator}
              onChange={(e) => setLevelComparator(e.target.value)}
              label="Level Comparison"
              disabled={!selectedLevel}
              sx={{
                backgroundColor: 'white',
                height: '40px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#D1D5DB',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563eb',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563eb',
                },
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
            >
              {COMPARISON_OPERATOR_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div style={{ width: '25%' }}>
          <Autocomplete
            options={levelsLoading ? [] : languageLevels.sort((a, b) => a.order - b.order)}
            getOptionLabel={(option) => `${option.level} (${option.description})`}
            value={selectedLevel}
            onChange={(_, newValue) => {
              setSelectedLevel(newValue);
              // Reset level comparator to default when level is cleared
              if (!newValue) {
                setLevelComparator(DEFAULT_COMPARISON_OPERATOR);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Level"
                placeholder="Select a level..."
                sx={{
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 0',
                  }
                }}
              />
            )}
            loading={levelsLoading}
            loadingText="Loading levels..."
            sx={{
              '& .MuiChip-root': {
                backgroundColor: '#EFF6FF',
                color: '#2563EB'
              }
            }}
          />
        </div>
      </div>

      {/* Third Row - Skills Filter */}
      <div style={{ display: 'flex', gap: '16px', width: '100%', alignItems: 'flex-end' }}>
        <div style={{ width: '100%' }}>
          <SkillsFilter
            selectedSkills={selectedSkills}
            onSkillsChange={setSelectedSkills}
            placeholder="Select Radar Skills ..."
          />
        </div>
      </div>
    </div>
  );
};

export default CVRepositoryFilters; 